import { ReactElement } from "react";
//#region types
import { KeyValue } from "models/utilTypes/keyValue";
import { CurrencyEnums } from "models/enums";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { SalaryCostBreakDownSummaryViewProps } from "models/propTypes/wintSalaryPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DetailsTableViewSmall, Skeletons } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const SalaryCostBreakDownSummary: React.FC<SalaryCostBreakDownSummaryViewProps> = (props) => {
  const {
    totalSalaryCost,
    monthlyDeviationPercentage,
    netSalaryCost,
    socialFeesCost,
    personalTaxesCost,
    payoutDate,
    loading,
    netToApproveNow,
    netAlreadyApproved,
    payoutDateLabel = TranslationKeys.SalaryDeviation.WintTodoPayoutDate,
    payoutWhen = LocalizationHelperInstance.getFormattedDate(payoutDate),
  } = props;

  const hasAlreadyApprovedExtras = (netAlreadyApproved ?? 0) > 0;
  const toApprove = netToApproveNow ?? netSalaryCost;

  const formattedCurrency = (amount: number) =>
    LocalizationHelperInstance.getFormattedCurrency(amount, CurrencyEnums.CurrencyCode.SEK);

  const netSalaryRows: Array<KeyValue<string, string | ReactElement>> = hasAlreadyApprovedExtras
    ? [
        new KeyValue(
          `${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WintTodoNetSalaryToApprove
          )} (${LocalizationHelperInstance.getFormattedMessage(payoutDateLabel)} ${payoutWhen})`,
          formattedCurrency(toApprove)
        ),
        new KeyValue(
          `${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WintTodoAlreadyApprovedExtras
          )} (${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WintTodoOwnPayoutDates
          )})`,
          formattedCurrency(netAlreadyApproved ?? 0)
        ),
      ]
    : [
        new KeyValue(
          `${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WintTodoNetSalary
          )} (${LocalizationHelperInstance.getFormattedMessage(payoutDateLabel)} ${payoutWhen})`,
          formattedCurrency(netSalaryCost)
        ),
      ];

  const tableData: Array<KeyValue<string, string | ReactElement> | typeof ACTION_SEPARATOR> = [
    ...netSalaryRows.flatMap((row) => [row, ACTION_SEPARATOR] as const),
    new KeyValue(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoSocialFees),
      formattedCurrency(socialFeesCost)
    ),
    ACTION_SEPARATOR,
    new KeyValue(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoPersonalTaxes),
      formattedCurrency(personalTaxesCost)
    ),
    ACTION_SEPARATOR,
  ];

  return (
    <Grid container direction="column" rowGap={0.5} className={cssExports.salaryCostBreakDownSummary}>
      {loading ? (
        <Skeletons.CompactTextLinesSkeleton />
      ) : (
        <>
          <Grid item className={cssExports.titleLabel}>
            {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoSumTitle)}:`}
          </Grid>
          <Grid item className={cssExports.totalCost}>
            {LocalizationHelperInstance.getFormattedCurrency(totalSalaryCost, CurrencyEnums.CurrencyCode.SEK)}
          </Grid>
          <Grid item className={cssExports.deviation}>
            {`${LocalizationHelperInstance.getFormattedPercentage(
              monthlyDeviationPercentage,
              0,
              1,
              'always'
            )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoCompareWithPreviousMonth)}`}
          </Grid>
        </>
      )}
      <Grid container direction="column" className={cssExports.salaryInfo}>
        <Grid item className={clsx(cssExports.titleLabel, cssExports.truncateString)}>
          {loading ? (
            <></>
          ) : (
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WintTodoSalaryBreakdownTitle)
          )}
        </Grid>
        <Grid item>
          {loading ? (
            <Skeletons.DetailTableViewSmallSkeleton />
          ) : (
            <DetailsTableViewSmall
              className={cssExports.salaryTableDetails}
              hideButton={true}
              detailsList={tableData}
              valueAlignment="right"
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SalaryCostBreakDownSummary;
